/* eslint-disable prettier/prettier */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import * as VideoExpress from '@vonage/video-express'



type VideoCallContextType = {
  roomsMap: RoomsMap
  getRoomState: (roomId: string) => VideoRoomState | undefined
  setRoomState: (roomId: string, state: Partial<VideoRoomState>) => void
  onLeave: (roomId: string) => void
}

const VideoCallContext = createContext<VideoCallContextType | undefined>(undefined)

type VideoRoomState = {
  showVideoRoom: boolean
  miniPlayer: boolean
  videoRoomProps: any
  roomRef: React.MutableRefObject<VideoExpress.Room | null>
  listOfMessages: Array<{
    data: string
    isSentByMe: boolean
    from: any
    date: string
  }>
  unreadCount: number 
  prevMessageCount: number 
}

type RoomsMap = Map<string, VideoRoomState>

const VideoCallProvider: React.FC = ({ children }) => {
  const [roomsMap, setRoomsMap] = useState<RoomsMap>(new Map())

  const setRoomState = useCallback((roomId: string, state: Partial<VideoRoomState>) => {
    setRoomsMap(prev => {
      const newMap = new Map(prev)
      const currentRoom = newMap.get(roomId) || {
        showVideoRoom: false,
        miniPlayer: false,
        videoRoomProps: null,
        roomRef: { current: null },
        listOfMessages: [],
        unreadCount: 0,
        prevMessageCount: 0
      }
      newMap.set(roomId, { ...currentRoom, ...state })
      return newMap
    })
  }, [])

  const getRoomState = useCallback((roomId: string) => {
    return roomsMap.get(roomId)
  }, [roomsMap])

  const onLeave = useCallback((roomId: string) => {
    setRoomsMap(prev => {
      const newMap = new Map(prev)
      newMap.delete(roomId)
      return newMap
    })
  }, [])

  return (
    <VideoCallContext.Provider value={{
      roomsMap, 
      getRoomState,
      setRoomState, 
      onLeave
    }}
    >
      {children}
    </VideoCallContext.Provider>
  )
}

function useVideoCall(roomId: string) {
const context = useContext(VideoCallContext)
 if (!context) {
    throw new Error('useVideoCall must be used within a VideoCallProvider')
  }
  const { roomsMap, getRoomState, setRoomState, onLeave } = context

  const [roomState, setRoomStateLocal] = useState(() => getRoomState(roomId))

  useEffect(() => {
    setRoomStateLocal(getRoomState(roomId))
    
  }, [roomsMap, roomId])

  return {
    roomState,
    setRoomState: (state: Partial<VideoRoomState>) => setRoomState(roomId, state),
    onLeave: () => onLeave(roomId)
  }
}

export { VideoCallContext, VideoCallProvider, useVideoCall }

